import { useState, useCallback } from "react";

import { isApolloError } from "@apollo/client";
import { datadogRum } from "@datadog/browser-rum";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import {
  CollaborativeInboundProgressedAction,
  CollaborativeInboundStage,
  CollaborativeInboundUpdateState,
} from "analytics/events";
import { useEmployeeStore } from "app/stores/useEmployeeStore";
import { routes } from "config/routes";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { truncateEmployeeName } from "utils/formats";
import { isNullOrUndefined } from "utils/tsHelpers";

import { DeliveryType, InboundStoreContext, InboundUnitAddOrigin } from "./inboundStore/types";
import { useInboundStore } from "./inboundStore/useInboundStore";
import {
  getMatchedDesadvId,
  mapDeliveryTypeToInboundingType,
  selectDespatchAdviceItemBySku,
  sumStockUpdatePlanTotal,
} from "./inboundStore/utils";
import { useInboundPreviewService } from "./useInboundPreviewService";
import {
  INBOUND_UNIT_ADD_ORIGIN_MAP,
  useInboundEventTracker,
} from "../hooks/useInboundEventTracker/useInboundEventTracker";
import {
  useAddProductsToInboundingListMutation,
  useCreateInboundingListMutation,
  useShareInboundingListMutation,
} from "../queries/collaborativeInbound/collaborativeInbound.generated";
import { useGetMultipleDesadvsRolliIdLazyQuery } from "../queries/despatchAdvice/despatchAdvice.generated";

const CREATE_SHARED_LIST_ERROR_TOAST_ID = "create_shared_list_error_toast";
const CREATE_SHARED_LIST_SUCCESS_TOAST_ID = "create_shared_list_success_toast";
const ADD_ITEMS_SHARED_LIST_ERROR_TOAST_ID = "add_items_shared_list_error_toast";
const SHARE_LIST_SUCCESS_TOAST_ID = "share_list_success_toast";
const SHARE_LIST_ERROR_TOAST_ID = "share_list_error_toast";

const formatSharedListName = (displayName: string) => {
  const formattedName = truncateEmployeeName(displayName);
  const currentTimeInGerman = new Date().toLocaleTimeString("de-DE").slice(0, 5);
  return `${formattedName}'s list - ${currentTimeInGerman}`;
};

const processInboundUnitStockUpdate = ({
  productSku,
  inboundUnitsStockUpdates,
  inboundUnitsDisplayStates,
  inboundUnitsMap,
  deliverySSCC,
  despatchAdviceItems,
}: Pick<
  InboundStoreContext,
  | "inboundUnitsStockUpdates"
  | "inboundUnitsDisplayStates"
  | "inboundUnitsMap"
  | "deliverySSCC"
  | "despatchAdviceItems"
> & { productSku: string }) => {
  const getStockUpdatePlanTotal = (sku: string) =>
    sumStockUpdatePlanTotal(inboundUnitsStockUpdates[sku]?.stockUpdatePlan);

  const getDespatchAdviceItemDetails = (sku: string) => {
    const despatchAdviceItem = selectDespatchAdviceItemBySku(sku, despatchAdviceItems);
    return {
      desadvId: getMatchedDesadvId(sku, despatchAdviceItems),
      totalQuantity: despatchAdviceItem?.totalQuantity?.toString(),
      unitType: despatchAdviceItem?.unitType,
    };
  };

  const getMetadata = (sku: string, despatchAdviceItemDetails: any, scannedEan: string | null) => ({
    desadv: despatchAdviceItemDetails.desadvId,
    expectedQuantity: despatchAdviceItemDetails.totalQuantity,
    inboundingMethod: inboundUnitsMap[sku]?.inboundingMethod,
    inboundingUnit: despatchAdviceItemDetails.unitType,
    scannedEan,
    sscc: deliverySSCC,
  });

  const handlingUnitSize = inboundUnitsDisplayStates[productSku]?.unitSizeForDisplay || 1;
  const stockUpdatePlanTotal = getStockUpdatePlanTotal(productSku);
  const despatchAdviceItemDetails = getDespatchAdviceItemDetails(productSku);
  const { ean = "", inboundingMethod } = inboundUnitsMap[productSku] || {};
  const scannedEan = inboundingMethod === "scan" ? ean : null;
  const metadata = getMetadata(productSku, despatchAdviceItemDetails, scannedEan);

  return {
    sku: productSku,
    quantity: stockUpdatePlanTotal,
    handlingUnitSize,
    metadata,
  };
};

const getInboundUnitsStockUpdates = ({
  inboundUnitsSortedByDate,
  inboundUnitsStockUpdates,
  inboundUnitsDisplayStates,
  inboundUnitsMap,
  deliverySSCC,
  despatchAdviceItems,
}: Pick<
  InboundStoreContext,
  | "inboundUnitsStockUpdates"
  | "inboundUnitsSortedByDate"
  | "inboundUnitsDisplayStates"
  | "inboundUnitsMap"
  | "deliverySSCC"
  | "despatchAdviceItems"
>) => {
  return inboundUnitsSortedByDate.map((sku) =>
    processInboundUnitStockUpdate({
      productSku: sku,
      inboundUnitsStockUpdates,
      inboundUnitsDisplayStates,
      inboundUnitsMap,
      deliverySSCC,
      despatchAdviceItems,
    }),
  );
};

export const useSharedListCreator = () => {
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();
  const { sendSegmentTrackEvent } = useAnalytics();
  const { sendInboundStateUpdate } = useInboundEventTracker();

  const [isCreatingList, setIsCreatingList] = useState(false);
  const [isAddingItems, setIsAddingItems] = useState(false);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isSharingList, setIsSharingList] = useState(false);

  const {
    deliveryType,
    sharedListId,
    sharedListName,
    setDeliverySSCC,
    setSharedListName,
    setSharedListId,
    setSelectedOption,
    setMatchedCategorizedList,
    updateDespatchAdviceItems,
    setInboundUIState,
    resetPreDroppingState,
  } = useInboundStore(
    useShallow((state) => ({
      deliveryType: state.deliveryType,
      sharedListId: state.sharedListId,
      sharedListName: state.sharedListName,
      setDeliverySSCC: state.setDeliverySSCC,
      setSharedListName: state.setSharedListName,
      setSharedListId: state.setSharedListId,
      setSelectedOption: state.setSelectedOption,
      setMatchedCategorizedList: state.setMatchedCategorizedList,
      updateDespatchAdviceItems: state.updateDespatchAdviceItems,
      setInboundUIState: state.setInboundUIState,
      resetPreDroppingState: state.resetPreDroppingState,
    })),
  );

  const { displayName, badgeNo } = useEmployeeStore(
    useShallow((state) => ({
      displayName: state.displayName,
      badgeNo: state.badgeNo,
    })),
  );

  const listName = formatSharedListName(displayName || badgeNo || "");

  const [fetchDesadvs] = useGetMultipleDesadvsRolliIdLazyQuery({
    onError: (e) => {
      if (
        isApolloError(e) &&
        e.graphQLErrors?.[0]?.extensions?.code === "DESPATCH_ADVICE_NOT_FOUND"
      ) {
        setInboundUIState({ isDespatchAdviceModalVisible: true });
      } else {
        const errorMessage = e instanceof Error ? e.message : "Unknown error";
        datadogRum.addError("Create Shared List Error", { errorMessage });
      }
    },
  });

  const [createSharedListMutation] = useCreateInboundingListMutation({
    onCompleted: (data) => {
      const listId = data?.createInboundingList?.id || null;
      setSharedListName(listName);
      setSharedListId(listId);
      navigate(routes.inbound.preDropping);
    },
  });

  const [addItemsToPreDroppingListMutation] = useAddProductsToInboundingListMutation();

  const [shareInboundingListMutation] = useShareInboundingListMutation();

  const { fetchInboundPreviewProducts } = useInboundPreviewService();

  const createSharedList = useCallback(
    async (inboundingType: DeliveryType | null, sscc?: string) => {
      setIsCreatingList(true);
      setDeliverySSCC(sscc || null);
      try {
        if (!isNullOrUndefined(sscc)) {
          const { data } = await fetchDesadvs({ variables: { input: { rolliID: sscc } } });
          // If the fetch fails or there's no data, return early, Do not proceed with creating the shared list
          if (!data) return;
          updateDespatchAdviceItems(data.getMultipleDesadvsRolliID?.despatchAdvices ?? null);
          showToastUI({
            status: "success",
            id: CREATE_SHARED_LIST_SUCCESS_TOAST_ID,
            title: "create_shared_list_success_toast",
          });
        }
        const mappedInboundingType = mapDeliveryTypeToInboundingType(inboundingType);
        const { data: sharedListData } = await createSharedListMutation({
          variables: { input: { name: listName, sscc, inboundingType: mappedInboundingType } },
        });
        sendInboundStateUpdate({
          state: CollaborativeInboundUpdateState.ListPreparationStarted,
          droppingListId: sharedListData?.createInboundingList?.id,
          sscc,
          inboundingType,
          creatorId: "manual",
        });
      } catch (error) {
        showToastUI({
          id: CREATE_SHARED_LIST_ERROR_TOAST_ID,
          title: "create-shared-list-error-toast",
        });
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        datadogRum.addError("Create Shared List Error", { errorMessage });
      } finally {
        setIsCreatingList(false);
      }
    },
    [
      setDeliverySSCC,
      createSharedListMutation,
      listName,
      sendInboundStateUpdate,
      fetchDesadvs,
      updateDespatchAdviceItems,
      showToastUI,
    ],
  );

  const addItemsToSharedList = useCallback(async () => {
    setIsAddingItems(true);
    setInboundUIState({ isAddToSharedListModalVisible: false });
    try {
      if (!sharedListId) return;
      const {
        inboundUnitsSortedByDate,
        inboundUnitsStockUpdates,
        inboundUnitsDisplayStates,
        inboundUnitsMap,
        deliverySSCC,
        despatchAdviceItems,
      } = useInboundStore.getState();
      const products = getInboundUnitsStockUpdates({
        inboundUnitsSortedByDate,
        inboundUnitsStockUpdates,
        inboundUnitsDisplayStates,
        inboundUnitsMap,
        deliverySSCC,
        despatchAdviceItems,
      }).filter((item) => item.quantity !== 0);
      await addItemsToPreDroppingListMutation({
        variables: {
          input: {
            listId: sharedListId,
            products,
          },
        },
      });
      sendInboundStateUpdate({
        state: CollaborativeInboundUpdateState.ListPreparationFinished,
        droppingListId: sharedListId,
        sscc: deliverySSCC,
        inboundingType: deliveryType,
        creatorId: "manual",
      });
    } catch (error) {
      showToastUI({
        status: "error",
        id: ADD_ITEMS_SHARED_LIST_ERROR_TOAST_ID,
        title: "add_items_shared_list_error_toast",
      });
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Add Items Shared List Error", { errorMessage });
    } finally {
      setIsAddingItems(false);
    }
  }, [
    addItemsToPreDroppingListMutation,
    deliveryType,
    sendInboundStateUpdate,
    setInboundUIState,
    sharedListId,
    showToastUI,
  ]);

  const addItemToSharedList = useCallback(
    async (productSku: string) => {
      setIsAddingItem(true);
      setInboundUIState({ isAddToSharedListModalVisible: false });
      try {
        if (!sharedListId) return;
        const {
          inboundUnitsStockUpdates,
          inboundUnitsDisplayStates,
          inboundUnitsMap,
          deliverySSCC,
          despatchAdviceItems,
        } = useInboundStore.getState();
        const product = processInboundUnitStockUpdate({
          productSku,
          inboundUnitsStockUpdates,
          inboundUnitsDisplayStates,
          inboundUnitsMap,
          deliverySSCC,
          despatchAdviceItems,
        });
        if (product.quantity === 0) return;
        await addItemsToPreDroppingListMutation({
          variables: {
            input: {
              listId: sharedListId,
              products: [product],
            },
          },
        });
        const origin = inboundUnitsMap[productSku].inboundingMethod as InboundUnitAddOrigin;
        sendSegmentTrackEvent("inboundProgressed", {
          dropping_list_id: sharedListId,
          product_sku: productSku,
          action: CollaborativeInboundProgressedAction.ProductAddedToList,
          is_handling_unit: product.handlingUnitSize !== 1,
          method: INBOUND_UNIT_ADD_ORIGIN_MAP[origin],
          quantity: product.quantity,
          sscc: deliverySSCC,
          shelf_number: null,
          dropping_list_name: sharedListName,
          inbounding_stage: CollaborativeInboundStage.ListPreparation,
        });
      } catch (error) {
        showToastUI({
          status: "error",
          id: ADD_ITEMS_SHARED_LIST_ERROR_TOAST_ID,
          title: "add_items_shared_list_error_toast",
        });
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        datadogRum.addError("Add Item Shared List Error", { errorMessage });
      } finally {
        setIsAddingItem(false);
      }
    },
    [
      addItemsToPreDroppingListMutation,
      sendSegmentTrackEvent,
      setInboundUIState,
      sharedListId,
      sharedListName,
      showToastUI,
    ],
  );

  const shareInboundingList = useCallback(async () => {
    setIsSharingList(true);
    try {
      if (!sharedListId) return;
      const { data } = await shareInboundingListMutation({
        variables: {
          input: {
            listId: sharedListId,
          },
        },
      });
      if (data?.shareInboundingList.success) {
        const { deliverySSCC } = useInboundStore.getState();
        showToastUI({
          status: "success",
          id: SHARE_LIST_SUCCESS_TOAST_ID,
          title: "share_list_success_toast",
        });
        sendInboundStateUpdate({
          state: CollaborativeInboundUpdateState.ListPreparationFinished,
          droppingListId: sharedListId,
          sscc: deliverySSCC,
          inboundingType: deliveryType,
          creatorId: "manual",
        });
        resetPreDroppingState();
        navigate(routes.inbound.root);
      }
    } catch (error) {
      showToastUI({
        status: "error",
        id: SHARE_LIST_ERROR_TOAST_ID,
        title: "share_list_success_toast",
      });
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Share List Error", { errorMessage });
    } finally {
      setIsSharingList(false);
    }
  }, [
    deliveryType,
    navigate,
    resetPreDroppingState,
    sendInboundStateUpdate,
    shareInboundingListMutation,
    sharedListId,
    showToastUI,
  ]);

  const handleCreateSharedList = useCallback(
    async (inboundingType: DeliveryType | null, sscc?: string) => {
      const { categorizedLists } = useInboundStore.getState();
      const matchedCategorizedList = categorizedLists.unclaimed.find((list) => list.sscc === sscc);
      if (matchedCategorizedList?.id) {
        setMatchedCategorizedList({
          deliveryType: inboundingType,
          sscc,
          list: matchedCategorizedList,
        });
        setInboundUIState({ isSSCCMatchConfirmationModalVisible: true });
      } else {
        createSharedList(inboundingType, sscc);
      }
    },
    [createSharedList, setInboundUIState, setMatchedCategorizedList],
  );

  const handleSSCCMatchConfirmationModalContinue = useCallback(() => {
    const { matchedCategorizedList } = useInboundStore.getState();
    if (matchedCategorizedList) {
      createSharedList(matchedCategorizedList.deliveryType, matchedCategorizedList.sscc);
    }
    setInboundUIState({ isSSCCMatchConfirmationModalVisible: false });
  }, [createSharedList, setInboundUIState]);

  const handleSSCCMatchConfirmationModalCancel = useCallback(async () => {
    const { matchedCategorizedList } = useInboundStore.getState();
    if (matchedCategorizedList && matchedCategorizedList.list?.id) {
      setSharedListId(matchedCategorizedList.list.id);
      setSharedListName(matchedCategorizedList.list.name);
      setSelectedOption(deliveryType);
      await fetchInboundPreviewProducts(matchedCategorizedList.list.products);
    }
    setInboundUIState({ isSSCCMatchConfirmationModalVisible: false });
  }, [
    deliveryType,
    fetchInboundPreviewProducts,
    setInboundUIState,
    setSelectedOption,
    setSharedListId,
    setSharedListName,
  ]);

  const handleDespatchAdviceModalContinue = useCallback(async () => {
    setInboundUIState({ isDespatchAdviceModalVisible: false });
    await createSharedList(deliveryType);
  }, [createSharedList, deliveryType, setInboundUIState]);

  const handleDespatchAdviceModalRetry = useCallback(() => {
    setInboundUIState({ isDespatchAdviceModalVisible: false });
    setDeliverySSCC(null);
  }, [setDeliverySSCC, setInboundUIState]);

  const handleSharingList = useCallback(async () => {
    await shareInboundingList();
  }, [shareInboundingList]);

  return {
    isCreatingList,
    isAddingItems,
    isAddingItem,
    isSharingList,
    createSharedList,
    addItemsToSharedList,
    addItemToSharedList,
    handleCreateSharedList,
    handleSSCCMatchConfirmationModalContinue,
    handleSSCCMatchConfirmationModalCancel,
    handleDespatchAdviceModalContinue,
    handleDespatchAdviceModalRetry,
    handleSharingList,
  };
};
